import React, { useCallback } from 'react';
import CSVReader from 'react-csv-reader';
import { Typography } from '@mui/material';
import { AddMemberTable } from './add-member-table';
import { AddMemberRow, Row, Role as MappedRole } from './add-member-row';
import MUIAlert from '../shared/mui-alert';
import Button from '../shared/button';
import css from '../shared/css';
import { ImportSchema } from '../shared/import-members';
import LicenseRequirementAlert from './license-requirement-alert';

interface Props {
  rows: Row[];
  roles: MappedRole[];
  rolesDefault?: MappedRole;
  isManaged: boolean;
  status: string;
  maxMembersReached: boolean;
  formError: boolean;
  importData?: ImportSchema;
  importErrorsFixed: boolean;
  onUpdateRow: (updatedRow: Row) => void;
  onAddNewRow: () => void;
  onRemoveRow: (id: string) => void;
  onUploadFile: (data: any[]) => void;
  displayTooltip: () => string;
  renderErrors: (erroredMembers: any[]) => JSX.Element;
  renderChanges: (changes: string[]) => JSX.Element;
  availableLicenses: number;
  minimumLicensePurchase?: number;
}

const parserOptions = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: 'greedy', // skips empty rows
  transformHeader: (header: string) => header.toLowerCase().replaceAll(/\W/g, '_'), // replaces any non-word character
};

const AddMembers: React.FC<Props> = ({
  rows,
  roles,
  rolesDefault,
  isManaged,
  status,
  maxMembersReached,
  formError,
  importData,
  importErrorsFixed,
  onUpdateRow,
  onAddNewRow,
  onRemoveRow,
  onUploadFile,
  displayTooltip,
  renderErrors,
  renderChanges,
  availableLicenses,
  minimumLicensePurchase,
}) => {
  const handleUploadFile = useCallback(async (data: any[]) => {
    onUploadFile(data);
  }, [onUploadFile]);

  function renderImportButton(): string {
    switch (status) {
      case 'pending':
        return 'Processing...';
      case 'complete':
        return 'Members Imported';
      default:
        return 'Import CSV';
    }
  }

  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>1. Add Members to your Organization</Typography>
      <Typography variant="body2">
        Invite new members via email. Each invite contains a link for members to create
        their own password.
      </Typography>
      {importData && !!importData.success.length && (
        <MUIAlert severity="success">
          {`Successfully imported ${importData.success.length} out of ${importData.all.length} members`}
        </MUIAlert>
      )}
      {importData && !!importData.errors.length && !importErrorsFixed && (
        <MUIAlert severity="error">
          <strong>There were errors. Please fix to continue</strong>
          {renderErrors(importData.errors)}
        </MUIAlert>
      )}
      {importData && !!importData.changes.length && (
        <MUIAlert severity="info">{renderChanges(importData.changes)}</MUIAlert>
      )}
      <MUIAlert severity="info">
        Assigning users to your organization will deploy one license for each and will add new licenses to your
        subscription if necessary.
      </MUIAlert>
      <LicenseRequirementAlert
        members={rows}
        availableLicenses={availableLicenses}
        minimumLicensePurchase={minimumLicensePurchase}
      />
      {!!roles.length && (
        <AddMemberTable>
          {rows.map((row) => (
            <AddMemberRow
              key={row._id}
              rowID={row._id}
              rowData={row}
              userRoles={roles}
              defaultRole={rolesDefault}
              errors={row.errors ?? []}
              isManaged={isManaged}
              roleID={row.role_id}
              disabled={!!row.existing_user}
              isOnlyRow={rows.length === 1}
              removeRow={() => onRemoveRow(row._id)}
              updateRow={(input) => onUpdateRow(input)}
              imported={!!row.imported}
            />
          ))}
        </AddMemberTable>
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          small
          affirmOutlined
          disabled={maxMembersReached || formError}
          tooltip={(formError || maxMembersReached) ? displayTooltip() : undefined}
          tooltipDelay={0}
          onClick={() => onAddNewRow()}
        >
          Add another member
        </Button>
        {status !== 'complete' && (
          <>
            <span style={{ padding: '0 5px 0 0' }}>or</span>
            <Button
              small
              affirmOutlined
              disabled={status === 'pending'}
              tooltip="Column headers required: email, first_name, last_name, and role"
              tooltipDelay={0}
              onClick={() => document?.getElementById('inputFile')?.click()}
            >
              {renderImportButton()}
            </Button>
          </>
        )}
        <CSVReader
          cssClass={styles.input}
          accept=".csv"
          inputId="inputFile"
          onFileLoaded={handleUploadFile}
          parserOptions={parserOptions}
        />
      </div>
    </>
  );
};

const styles = css<React.CSSProperties & { input: string }>({
  input: {
    visibility: 'hidden',
  },
});

export default AddMembers;
